import React, { useState, useEffect, useMemo } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { RRule, RRuleSet, rrulestr, Weekday } from 'rrule'; // Import rrule.js
import Datepicker from 'react-tailwindcss-datepicker';

// --- Configuration ---
const frequencies = [
  { value: 'monthly', label: 'Monthly', rruleFreq: RRule.MONTHLY, interval: 1, requires: 'dayOfMonth' },
  { value: 'biweekly', label: 'Bi-Weekly (Every 2 Weeks)', rruleFreq: RRule.WEEKLY, interval: 2, requires: 'dayOfWeek' },
  { value: 'quarterly', label: 'Quarterly', rruleFreq: RRule.MONTHLY, interval: 3, requires: 'dayOfMonth' },
  { value: 'semiannually', label: 'Semi-Annually', rruleFreq: RRule.MONTHLY, interval: 6, requires: 'dayOfMonth' },
  { value: 'yearly', label: 'Yearly', rruleFreq: RRule.YEARLY, interval: 1, requires: 'dayOfMonth' },
];

const MAX_DAY_OF_MONTH = 28;
const PREVIEW_COUNT = 5;

// Helper to get the RRule Weekday object (e.g., RRule.MO)
const getRruleWeekday = (jsDay) => {
  // JS Date: Sun=0, Mon=1, ..., Sat=6
  // RRule:   Mon=0, Tue=1, ..., Sun=6
  const rruleDayMap = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
  return rruleDayMap[jsDay];
};

// Helper to find the next date matching the criteria
// Renamed from findNextStartDate to findAdjustedStartDate to reflect new logic
const findAdjustedStartDate = (startDate, freqConfig, dayOfMonth, rruleWeekday) => {
  console.log("[findAdjustedStartDate] Input startDate:", startDate);
  console.log("[findAdjustedStartDate] Input freqConfig:", freqConfig);
  console.log("[findAdjustedStartDate] Input dayOfMonth:", dayOfMonth);
  console.log("[findAdjustedStartDate] Input rruleWeekday:", rruleWeekday);

  const now = new Date();
  now.setUTCHours(0, 0, 0, 0); // Compare dates only

  // Check if input startDate is valid initially
  if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
    console.error("[findAdjustedStartDate] Invalid startDate passed to function:", startDate);
    // Decide how to handle this - maybe return null or throw an error?
    // For now, let's return null to indicate failure.
    return null;
  }

  let dt = new Date(startDate);
  dt.setUTCHours(0, 0, 0, 0);
  console.log("[findAdjustedStartDate] Initial dt (UTC):", dt.toISOString());

  // Adjust based on frequency requirements
  if (freqConfig.requires === 'dayOfMonth') {
    // Only adjust if the current day doesn't match
    // Note: We rely on the MAX_DAY_OF_MONTH validation elsewhere to prevent invalid dates (e.g., Feb 30)
    if (dt.getUTCDate() !== dayOfMonth) {
      dt.setUTCDate(dayOfMonth);
      console.log("[findAdjustedStartDate] dt after setUTCDate(dayOfMonth):", dt.toISOString());
    }
  } else { // dayOfWeek
    const targetJsDay = (rruleWeekday.weekday + 1) % 7; // Convert RRule day back to JS day (Sun=0...Sat=6)
    // Only adjust if the current day doesn't match the target weekday
    if (dt.getUTCDay() !== targetJsDay) {
      let safety = 0; // Prevent infinite loop
      // Advance day by day until the weekday matches
      while (dt.getUTCDay() !== targetJsDay && safety < 8) {
        dt.setUTCDate(dt.getUTCDate() + 1);
        safety++;
      }
      console.log("[findAdjustedStartDate] dt after weekday adjustment loop:", dt.toISOString(), "Safety iterations:", safety);
      if (safety >= 8) {
        console.error("[findAdjustedStartDate] Weekday adjustment loop safety break triggered.");
        return null; // Indicate failure
      }
    }
  }

  // --- Removed the tempRule and .after(now) logic ---
  // The goal is to return the user's chosen date, adjusted *only* for day/weekday rules,
  // not advanced based on the current date.

  // Final validation and return
  if (isNaN(dt.getTime())) {
    console.error("[findAdjustedStartDate] dt became invalid after adjustments!", dt);
    return null; // Indicate failure
  }
  console.log("[findAdjustedStartDate] Final adjusted dt:", dt.toISOString());
  return dt; // Return the adjusted date directly

};

// --- Component ---
const ScheduleSelector = ({
  label = 'Select Schedule Frequency & Start Date',
  fieldName = 'ical_rrule',
  initialFundRruleIcal,
  onIcalChange
}) => {

  // --- State ---
  const [selectedFrequencyValue, setSelectedFrequencyValue] = useState(frequencies[0].value);
  const [selectedDateValue, setSelectedDateValue] = useState(null); // e.g., { startDate: 'YYYY-MM-DD', endDate: '...' }
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [currentRruleIcal, setCurrentRruleIcal] = useState('');
  const [dateError, setDateError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // --- Memoized Values ---
  const selectedFrequencyConfig = useMemo(() =>
    frequencies.find(f => f.value === selectedFrequencyValue) || frequencies[0],
    [selectedFrequencyValue]
  );

  // --- Effects ---

  // Initialize from initialFundRruleIcal or set default
  useEffect(() => {
    let initialFreqVal = frequencies[0].value;
    let initialDate = null;
    let initialIcal = '';

    if (initialFundRruleIcal) {
      try {
        const rule = rrulestr(initialFundRruleIcal);
        const opts = rule instanceof RRuleSet ? rule.rrules()[0]?.options : rule.options;

        if (opts) {
          const freqMatch = frequencies.find(f => f.rruleFreq === opts.freq && f.interval === (opts.interval || 1));
          if (freqMatch) {
            initialFreqVal = freqMatch.value;
            const dtstart = opts.dtstart;
            let day = null;
            if (freqMatch.requires === 'dayOfMonth' && opts.bymonthday && opts.bymonthday.length > 0) {
              day = opts.bymonthday[0];
            } else if (freqMatch.requires === 'dayOfWeek' && opts.byweekday && opts.byweekday.length > 0) {
              // Need JS weekday from RRule weekday for initial display
              // RRule: MO=0..SU=6 , Date: SU=0..SA=6
              const jsDay = (opts.byweekday[0] + 1) % 7;
              day = jsDay; // We'll extract the weekday again later
            }

            if (dtstart && day !== null) {
              // Set the date picker to the dtstart date, ensuring the day aligns with rules
              const initialPickerDate = new Date(dtstart);
              initialPickerDate.setUTCHours(0, 0, 0, 0);
              // Validation check for initial load
              const initialDayOfMonth = initialPickerDate.getUTCDate();
              if (freqMatch.requires === 'dayOfMonth' && initialDayOfMonth > MAX_DAY_OF_MONTH) {
                setDateError(`Initial schedule day (${initialDayOfMonth}) is invalid (must be <= ${MAX_DAY_OF_MONTH}). Please select a new date.`);
                // Keep freq, but null date
              } else if (freqMatch.requires === 'dayOfWeek' && initialDayOfMonth > MAX_DAY_OF_MONTH && new Date(Date.UTC(initialPickerDate.getUTCFullYear(), initialPickerDate.getUTCMonth() + 1, 0)).getUTCDate() >= initialDayOfMonth) {
                // This case is tricky - if rule is biweekly Mon, but dtstart happens
                // to be Mon 30th, it's technically valid for rrule, but our UI constraint forbids it.
                // For simplicity, we enforce the <=28 constraint universally on the *picker*
                setDateError(`Initial schedule start day (${initialDayOfMonth}) is invalid (must be <= ${MAX_DAY_OF_MONTH}). Please select a new date.`);
                // Keep freq, but null date
              } else {
                initialDate = { startDate: initialPickerDate.toISOString().split('T')[0], endDate: initialPickerDate.toISOString().split('T')[0] };
                initialIcal = initialFundRruleIcal; // Keep original if valid
              }
            }
          }
        }
      } catch (e) {
        console.error("Error parsing initial RRULE string:", initialFundRruleIcal, e);
        setDateError("Could not parse initial schedule. Please select frequency and date.");
      }
    }

    // Set default date if none derived from initial prop
    if (!initialDate) {
      const nextMonth = new Date();
      nextMonth.setUTCHours(0, 0, 0, 0);
      nextMonth.setUTCDate(1); // Start with 1st
      nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
      initialDate = { startDate: nextMonth.toISOString().split('T')[0], endDate: nextMonth.toISOString().split('T')[0] };
    }

    setSelectedFrequencyValue(initialFreqVal);
    setSelectedDateValue(initialDate);
    setCurrentRruleIcal(initialIcal); // May be empty if default or invalid
    setIsInitialized(true);

  }, [initialFundRruleIcal]); // Run only once on mount/prop change

  // Recalculate RRULE, iCal string, and preview when frequency or date changes
  useEffect(() => {
    if (!isInitialized || !selectedDateValue || dateError) {
      // Don't calculate if initializing, no date selected, or date is invalid
      if (!dateError) {
        setUpcomingDates([]); // Clear preview if no date
        setCurrentRruleIcal('');
        if (onIcalChange) onIcalChange('');
      }
      return;
    }

    // --- Add Validation for selectedDateValue ---
    if (!selectedDateValue || !selectedDateValue.startDate) {
      console.error("[ScheduleSelector] useEffect - selectedDateValue or startDate is missing:", selectedDateValue);
      // Optionally clear state or set an error
      // setDateError("Please select a valid start date.");
      setUpcomingDates([]);
      setCurrentRruleIcal('');
      if (onIcalChange) onIcalChange('');
      return; // Prevent further processing
    }
    // --- End Validation ---

    try {
      // Now it should be safer to create the Date object
      // --- Replace direct string concatenation with Date.UTC ---
      const [year, month, day] = selectedDateValue.startDate.split('-').map(Number);
      // Create date using Date.UTC (month is 0-indexed)
      const selectedTimestamp = Date.UTC(year, month - 1, day);
      const selectedDate = new Date(selectedTimestamp);
      // --- End Date.UTC change ---

      // --- Add validation for the created Date object ---
      if (isNaN(selectedDate.getTime())) {
        // This check might still be useful if Date.UTC somehow fails, though less likely
        console.error("[ScheduleSelector] useEffect - Failed to create valid Date from selectedDateValue:", selectedDateValue.startDate);
        setDateError("Invalid date selected. Please choose again.");
        setUpcomingDates([]);
        setCurrentRruleIcal('');
        if (onIcalChange) onIcalChange('');
        return;
      }
      // --- End Date object validation ---

      const dayOfMonth = selectedDate.getUTCDate(); // Use getUTCDate() as it's a UTC date
      const jsDayOfWeek = selectedDate.getUTCDay(); // Use getUTCDay()
      const rruleWeekday = getRruleWeekday(jsDayOfWeek);

      // Calculate the actual start date (next occurrence from today)
      // Pass the correctly parsed UTC date object
      // Use the new function to get the adjusted start date
      const dtstart = findAdjustedStartDate(
        selectedDate,
        selectedFrequencyConfig,
        dayOfMonth,
        rruleWeekday
      );

      // --- Debugging Logs ---
      console.log('[ScheduleSelector] Calculated dtstart:', dtstart);
      console.log('[ScheduleSelector] Type of dtstart:', typeof dtstart);
      let isValidDtstart = false;
      if (dtstart instanceof Date) {
        isValidDtstart = !isNaN(dtstart.getTime());
        console.log('[ScheduleSelector] Is dtstart valid Date?:', isValidDtstart);
        console.log('[ScheduleSelector] dtstart ISO String:', dtstart.toISOString());
      } else {
        console.log('[ScheduleSelector] dtstart is not a Date object.');
      }
      // --- End Debugging Logs ---

      // Ensure dtstart is valid before proceeding
      if (!isValidDtstart) {
        console.error("[ScheduleSelector] Calculated dtstart is invalid. Aborting rule generation.");
        setDateError("Internal error: Could not determine a valid start date.");
        setUpcomingDates([]);
        setCurrentRruleIcal('');
        if (onIcalChange) onIcalChange('');
        return; // Stop processing if dtstart is bad
      }

      // Build RRule options
      const rruleOptions = {
        freq: selectedFrequencyConfig.rruleFreq,
        interval: selectedFrequencyConfig.interval,
        dtstart: dtstart, // Use the validated dtstart
      };

      // Add frequency-specific rules (BYMONTHDAY, BYWEEKDAY, BYMONTH)
      if (selectedFrequencyConfig.rruleFreq === RRule.YEARLY) {
        // For yearly, we need both month and day
        const monthOfYear = selectedDate.getUTCMonth() + 1; // JS month is 0-indexed, RRule is 1-indexed
        rruleOptions.bymonth = [monthOfYear];
        rruleOptions.bymonthday = [dayOfMonth];
      } else if (selectedFrequencyConfig.requires === 'dayOfMonth') {
        // For other frequencies requiring day of month (Monthly, Quarterly, Semi-Annually)
        rruleOptions.bymonthday = [dayOfMonth];
      } else { // 'dayOfWeek' (Bi-Weekly)
        rruleOptions.byweekday = [rruleWeekday];
      }

      // Generate Rule and Preview
      // Wrap the RRule creation in its own try/catch for detailed logging
      let rule;
      try {
        console.log('[ScheduleSelector] Attempting to create RRule with options:', rruleOptions);
        rule = new RRule(rruleOptions);
        console.log('[ScheduleSelector] RRule created successfully.');
      } catch (ruleError) {
        console.error("[ScheduleSelector] Error creating main RRule object:", ruleError);
        console.error("[ScheduleSelector] Options passed to RRule:", rruleOptions);
        // Rethrow or handle as appropriate, maybe set the error state
        throw ruleError; // Rethrow to be caught by the outer try/catch
      }

      const newIcalString = rule.toString();
      const previewEndDate = new Date(dtstart);
      // Estimate end date for preview (e.g., ~1 year out)
      previewEndDate.setUTCFullYear(previewEndDate.getUTCFullYear() + 1);

      // Get the first PREVIEW_COUNT occurrences including dtstart
      const nextDates = rule.between(dtstart, previewEndDate, true).slice(0, PREVIEW_COUNT);

      // Update state
      setCurrentRruleIcal(newIcalString);
      setUpcomingDates(nextDates);

      // Notify parent
      if (onIcalChange) {
        onIcalChange(newIcalString);
      }

    } catch (e) {
      // Outer catch block remains
      console.error("[ScheduleSelector] Error generating RRULE (outer catch):", e);
      setDateError("Error calculating schedule. Please check selections.");
      setUpcomingDates([]);
      setCurrentRruleIcal('');
      if (onIcalChange) onIcalChange('');
    }

    // Depend on initialized flag, error state, date, and frequency config
  }, [isInitialized, selectedFrequencyConfig, selectedDateValue, dateError, onIcalChange]);

  // --- Handlers ---
  const handleFrequencyChange = (newValue) => {
    setSelectedFrequencyValue(newValue);
  };

  const handleDateChange = (newValue) => {
    if (!newValue || !newValue.startDate) {
      setSelectedDateValue(null);
      setDateError(''); // Clear error if date is cleared
      setUpcomingDates([]);
      setCurrentRruleIcal('');
      if (onIcalChange) onIcalChange('');
      return;
    }

    // Validate Date (Day <= MAX_DAY_OF_MONTH)
    const selectedDay = parseInt(newValue.startDate.split('-')[2], 10);
    if (selectedDay > MAX_DAY_OF_MONTH) {
      setDateError(`Selected day (${selectedDay}) is invalid. Please choose a day between 1 and ${MAX_DAY_OF_MONTH}.`);
      setSelectedDateValue(newValue); // Keep invalid value shown in picker, but error prevents rule generation
      setUpcomingDates([]);
      setCurrentRruleIcal('');
      if (onIcalChange) onIcalChange('');
    } else {
      setDateError(''); // Clear error on valid date
      setSelectedDateValue(newValue);
      // useEffect will handle recalculation
    }
  };

  // --- Render ---
  if (!isInitialized) {
    return <div>Loading...</div>; // Or some loading indicator
  }

  return (
    <div className="space-y-4">
      <input type="hidden" name={fieldName} value={currentRruleIcal} />

      <label className="block text-sm font-medium leading-6 text-gray-900">{label}</label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Frequency Selector (Listbox) */}
        <Listbox
          value={selectedFrequencyValue} // Use the string value
          onChange={handleFrequencyChange} // Use the simple handler
        >
          <div className="relative w-full">
            <label className="block text-sm font-medium leading-6 text-gray-900">Invoicing Interval</label>
            <ListboxButton className="relative grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
              <span className="col-start-1 row-start-1 truncate pr-6">{selectedFrequencyConfig.label}</span>
              <ChevronUpDownIcon
                aria-hidden="true"
                className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
              />
            </ListboxButton>

            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
            >
              {frequencies.map((option) => ( // Map over original frequencies
                <ListboxOption
                  key={option.value}
                  value={option.value} // Use string value
                  className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white data-[focus]:outline-none"
                >
                  <span className="block truncate font-normal group-data-[selected]:font-semibold">
                    {option.label}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                    <CheckIcon aria-hidden="true" className="size-5" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {/* Date Picker */}
        <div className="w-full">
          <label className="block text-sm font-medium leading-6 text-gray-900">Start Date</label>
          <Datepicker
            useRange={false}
            asSingle={true}
            value={selectedDateValue}
            onChange={handleDateChange}
            placeholder={`Select Start ${selectedFrequencyConfig.requires === 'dayOfMonth' ? 'Day (1-28)' : 'Date'}`}
            inputClassName="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
            containerClassName="relative"
          // Note: react-tailwindcss-datepicker might not have granular disabled day logic (like *all* 29ths).
          // We handle this with validation in handleDateChange instead.
          // minDate={new Date()} // Optional: Prevent selecting past dates entirely
          />
          {dateError && (
            <p className="mt-1 text-sm text-red-600">{dateError}</p>
          )}
        </div>
      </div>

      {/* Upcoming Dates Preview */}
      {upcomingDates.length > 0 && !dateError && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Upcoming Dates Preview:</h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
            {upcomingDates.map((date, index) => (
              <li key={index}>{date.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ScheduleSelector;